import React from 'react'
import { Maybe } from 'graphql/jsutils/Maybe'
import { ControlledCheckbox, ControlledSelect, ControlledTextInput } from 'transfix-form/components'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box, capitalize, makeStyles } from 'transfix-ui/core'
import { ITransactionSetWithSubtype, ITransactionSetTypes } from 'types/graphqlTypes'
import { tfx12Config } from '../../EdiSelfService/types'
import { useConfigurableBuilderVersionForm } from '../hooks/useConfigurableBuilderVersionForm'
import { IConfigurableBuilderVersionSchema } from '../schema/ConfigurableBuilderVersionSchema'

interface IConfigurableBuilderVersionModalProps extends Omit<IModalBaseProps, 'modalType'> {
  isEdit: boolean
  version: IConfigurableBuilderVersionSchema
  onCancel: () => void
  onSubmit: (version: IConfigurableBuilderVersionSchema) => void
  stupidediConfigOptions: string[]
  tfx12ConfigOptions: tfx12Config[]
  transactionSetWithSubtype: ITransactionSetWithSubtype
}

const useConfigurableBuilderVersionModalStyles = makeStyles({
  body: {},
  modal: {},
  header: {},
  title: {},
  root: {},
})

const lookupTfx12ConfigName = (
  tfx12ConfigOptions: tfx12Config[],
  tfx12ConfigId?: Maybe<string>
) => {
  if (tfx12ConfigId) {
    const result = tfx12ConfigOptions.find(config => Number(config.id) === Number(tfx12ConfigId))
    return result?.reference || ''
  }
  return ''
}

const formatTfx12Configs = (configs: tfx12Config[]) => {
  return configs.map(config => {
    return {
      label: config.reference,
      value: config.reference,
    }
  })
}

const formatStupidediConfigs = (configs: string[]) => {
  return configs.map(element => {
    return {
      label: capitalize(element),
      value: element.toUpperCase(),
    }
  })
}

export const ConfigurableBuilderVersionModal = ({
  isEdit,
  onCancel,
  onSubmit,
  open = true,
  stupidediConfigOptions,
  tfx12ConfigOptions,
  transactionSetWithSubtype,
  version,
  ...props
}: IConfigurableBuilderVersionModalProps) => {
  const methods = useConfigurableBuilderVersionForm()
  const { control, errors, handleSubmit } = methods

  const submitHandler = handleSubmit((formParams: IConfigurableBuilderVersionSchema) => {
    onSubmit(formParams)
    onCancel()
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onCancel()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  const is210Builder = transactionSetWithSubtype.transactionSet === ITransactionSetTypes._210

  const classes = useConfigurableBuilderVersionModalStyles()
  // TODO Add field to set isLive (not currently allowed in input for creating versions)
  return (
    <ModalBase
      dataTest='ConfigurableBuilderOptionsModal'
      modalType={MODAL_TYPES.form}
      size={4}
      open={open}
      actions={actions}
      className={classes.root}
      bodyClassName={classes.body}
      titleClass={classes.title}
      modalClassName={classes.modal}
      headerClassName={classes.header}
      title={isEdit ? 'Edit Builder Version' : 'Create Builder Version'}
      {...props}>
      <Box mb={2}>
        <ControlledTextInput
          control={control}
          name='ediMapperConfigurableBuilderId'
          defaultValue={version.ediMapperConfigurableBuilderId}
          TextInputProps={{
            label: 'EDI Mapper Configurable Builder ID',
            required: true,
            large: true,
            disabled: true,
            validationMessage: errors?.ediMapperConfigurableBuilderId?.message,
            error: !!errors?.ediMapperConfigurableBuilderId?.message,
          }}
        />
        {isEdit && (
          <ControlledTextInput
            control={control}
            name='id'
            defaultValue={version.id}
            TextInputProps={{
              label: 'Builder Version ID',
              required: true,
              large: true,
              disabled: true,
              validationMessage: errors?.id?.message,
              error: !!errors?.id?.message,
            }}
          />
        )}
        <ControlledTextInput
          control={control}
          name='extractionTemplate'
          defaultValue={version.extractionTemplate}
          TextInputProps={{
            label: 'Extraction Template',
            required: true,
            large: true,
            disabled: true,
            validationMessage: errors?.extractionTemplate?.message,
            error: !!errors?.extractionTemplate?.message,
          }}
        />
        <ControlledTextInput
          control={control}
          name='builderLibrary'
          defaultValue={version.builderLibrary || 'STUPIDEDI'}
          TextInputProps={{
            label: 'Builder Library',
            required: true,
            large: true,
            validationMessage: errors?.builderLibrary?.message,
            error: !!errors?.builderLibrary?.message,
          }}
        />
        <ControlledSelect
          control={control}
          name='stupidediConfig'
          defaultValue={version.stupidediConfig}
          SelectProps={{
            label: 'Stupidedi Config',
            options: formatStupidediConfigs(stupidediConfigOptions),
            large: true,
          }}
        />
        <ControlledSelect
          control={control}
          name='tfx12DefinitionId'
          defaultValue={lookupTfx12ConfigName(tfx12ConfigOptions, version.tfx12DefinitionId)}
          SelectProps={{
            label: 'TFX12 Config',
            options: formatTfx12Configs(tfx12ConfigOptions),
            large: true,
          }}
        />
        {is210Builder && (
          <ControlledCheckbox
            control={control}
            name='canBuildBalanceDues'
            CheckboxProps={{ label: 'Can Build Balance Dues' }}
            defaultValue={version.canBuildBalanceDues}
          />
        )}
      </Box>
    </ModalBase>
  )
}
